import React, { useCallback, useMemo } from 'react';

import { deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaThumbsDown, FaThumbsUp, FaTrash } from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import { useHistory, useParams } from 'react-router-dom';

import notFoundImg from 'assets/undraw_not_found_white.svg';
import { useUserData } from 'hooks/auth';
import { Review } from 'interfaces/Review';
import { db } from 'services/firebase';

import {
  Container,
  ReviewContainer,
  ReviewText,
  AuthorProfileButton,
  ReviewVeredict,
  DeleteReviewButton,
  NoReviewsContainer,
} from './styles';

interface ReviewsProps {
  reviews: Review[];
  setReviews: React.Dispatch<React.SetStateAction<Review[]>>;
}

const Reviews: React.FC<ReviewsProps> = ({ reviews, setReviews }) => {
  const history = useHistory();

  const { serviceid } = useParams<{ serviceid: string }>();
  const { user } = useUserData();

  const orderedReviews = useMemo(() => {
    const sortedReviews = reviews.sort((prevReview, currReview) => {
      if (!!prevReview.author.id === !!currReview.author.id) return 0;
      if (prevReview.author.id) return -1;
      return 1;
    });

    const ownReviewIndex = sortedReviews.findIndex(
      (review) => review.author.id === user?.uid,
    );

    if (ownReviewIndex < 0) {
      return sortedReviews;
    }

    const newReviews = sortedReviews;

    [newReviews[ownReviewIndex], newReviews[0]] = [
      newReviews[0],
      newReviews[ownReviewIndex],
    ];

    return newReviews;
  }, [reviews, user]);

  const deleteReview = useCallback(
    async (review: Review) => {
      try {
        await deleteDoc(doc(db, `services/${serviceid}/reviews/${review.id}`));
        setReviews((oldReviews) =>
          oldReviews.filter((r) => r.id !== review.id),
        );
        toast.success('Avaliação removida com sucesso');
      } catch {
        toast.error('Erro ao remover sua avaliação. Tente novamente');
      }
    },
    [serviceid, setReviews],
  );

  const navigateToProfile = useCallback(
    (userId) => {
      if (!userId) return;

      history.push(`/profile/${userId}`);
    },
    [history],
  );

  return (
    <Container>
      {orderedReviews.length > 0 ? (
        orderedReviews.map((review) => (
          <ReviewContainer
            key={review.id}
            ownReview={review.author.id === user?.uid}
          >
            <AuthorProfileButton
              onClick={() => navigateToProfile(review.author.id)}
            >
              <p>{review.author.name}</p>
              {review.author.id && <MdVerified />}
            </AuthorProfileButton>
            {review.positive ? (
              <ReviewVeredict>
                <span style={{ color: '#2b992b' }}>Avaliação positiva</span>{' '}
                <FaThumbsUp color="#2b992b" size={18} />
              </ReviewVeredict>
            ) : (
              <ReviewVeredict>
                <span style={{ color: 'red' }}>Avaliação negativa</span>{' '}
                <FaThumbsDown color="red" size={18} />
              </ReviewVeredict>
            )}
            <ReviewText>
              <p>
                {review.message !== '' ? (
                  `${review.message}`
                ) : (
                  <i>
                    Este usuário não deixou um comentário, mas{' '}
                    {review.positive ? '' : <b>não</b>} recomenda este serviço.
                  </i>
                )}
              </p>
            </ReviewText>
            {review.author.id === user?.uid && (
              <DeleteReviewButton onClick={() => deleteReview(review)}>
                Remover sua avaliação
                <div style={{ width: 10 }} />
                <FaTrash />
              </DeleteReviewButton>
            )}
          </ReviewContainer>
        ))
      ) : (
        <NoReviewsContainer>
          <img src={notFoundImg} alt="Serviço sem recomendações" width={200} />
          <p>Ainda não foram postadas recomendações para este serviço</p>
        </NoReviewsContainer>
      )}
    </Container>
  );
};

export default Reviews;
